import { useState, useEffect, useCallback } from "react";
import emailjs from "@emailjs/browser";
import Button from "./Button";
import GoogleRecaptcha from "./GoogleRecaptcha";
import { company } from "../data/siteData";
import { trackEvent } from "../analytics";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMPTY = Object.freeze({ name: "", email: "", organization: "", reason: "", message: "" });
const ALLOWED_FIELDS = new Set(Object.keys(EMPTY));
const MAX_FIELD_LENGTH = 2000;

export default function ContactForm({ reasons }) {
  const [fields, setFields] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaResetSignal, setRecaptchaResetSignal] = useState(0);

  useEffect(() => {
    if (PUBLIC_KEY) {
      emailjs.init({ publicKey: PUBLIC_KEY });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!ALLOWED_FIELDS.has(name)) return;
    const sanitized = value.slice(0, MAX_FIELD_LENGTH);
    setFields((prev) => ({ ...prev, [name]: sanitized }));
  };

  const handleRecaptchaVerify = useCallback((token) => {
    setRecaptchaToken(token);
    setStatus((current) => (current === "captcha" ? "idle" : current));
  }, []);

  const handleRecaptchaExpire = useCallback(() => {
    setRecaptchaToken("");
  }, []);

  const handleRecaptchaError = useCallback(() => {
    setRecaptchaToken("");
  }, []);

  const resetRecaptcha = () => {
    setRecaptchaToken("");
    setRecaptchaResetSignal((value) => value + 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus("not-configured");
      return;
    }
    if (!fields.name.trim() || !fields.email.trim() || !fields.reason) {
      setStatus("validation");
      return;
    }
    if (!recaptchaToken) {
      setStatus("captcha");
      return;
    }
    setStatus("sending");
    emailjs
      .send(SERVICE_ID, TEMPLATE_ID, {
        from_name: fields.name.trim(),
        from_email: fields.email.trim(),
        organization: fields.organization.trim(),
        reason: fields.reason,
        message: fields.message.trim(),
        recaptcha_token: recaptchaToken,
      })
      .then(() => {
        setStatus("success");
        setFields(EMPTY);
        resetRecaptcha();
        // Track the reason (not the message content — PII)
        trackEvent("contact_form_submit", { reason: fields.reason });
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setStatus("error");
        trackEvent("contact_form_error", { reason: fields.reason });
      });
  };

  const sending = status === "sending";

  return (
    <div id="contact-form" className="contact-card contact-card--form">
      <p className="eyebrow">Contact Form</p>
      <h2>Tell us how BrainTorch™ Bio can help.</h2>
      <p>Fill out the form below and a member of the BrainTorch™ Bio team will be in touch.</p>
      <form className="contact-form" onSubmit={handleSubmit} noValidate>
        <div className="field-grid">
          <input type="text" name="name" placeholder="Name" value={fields.name} onChange={handleChange} required disabled={sending} autoComplete="name" />
          <input type="email" name="email" placeholder="Email" value={fields.email} onChange={handleChange} required disabled={sending} autoComplete="email" />
        </div>
        <input type="text" name="organization" placeholder="Organization" value={fields.organization} onChange={handleChange} disabled={sending} autoComplete="organization" />
        <select name="reason" value={fields.reason} onChange={handleChange} required disabled={sending}>
          <option value="" disabled>Inquiry Type</option>
          {reasons.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <textarea name="message" placeholder="Message" rows="5" value={fields.message} onChange={handleChange} disabled={sending} />
        <GoogleRecaptcha
          id="contact-recaptcha"
          label="Contact form security check"
          resetSignal={recaptchaResetSignal}
          onVerify={handleRecaptchaVerify}
          onExpire={handleRecaptchaExpire}
          onError={handleRecaptchaError}
        />
        <Button type="submit" variant="teal" disabled={sending}>
          {sending ? "Sending..." : "Submit Inquiry"}
        </Button>
        {status === "success" && (
          <div className="form-success">Thank you! Your message has been sent. We will be in touch shortly.</div>
        )}
        {status === "validation" && (
          <div className="form-error">Please fill out all required fields before submitting.</div>
        )}
        {status === "captcha" && (
          <div className="form-error">Please complete the ReCAPTCHA security check before submitting.</div>
        )}
        {status === "not-configured" && (
          <div className="form-error">
            The contact form is not yet configured. Please email us directly at{" "}
            <a href={`mailto:${company.email}`}>{company.email}</a>.
          </div>
        )}
        {status === "error" && (
          <div className="form-error">
            Something went wrong. Please try again or email us at{" "}
            <a href={`mailto:${company.email}`}>{company.email}</a>.
          </div>
        )}
      </form>
    </div>
  );
}
